/**
 * DeleteConfirmationDialog
 *
 * Purpose: Reusable dialog for confirming destructive operations
 *          Requires user to type the item name to enable deletion
 *
 * Architecture: Used by Tenant Manager and potentially other admin components
 *
 * Dependencies: @mui/material
 * Author: Development Team
 * Created: 2025-01-29
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Alert,
  TextField,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SecondaryButton } from './ActionButtons';
import DialogFooter from './DialogFooter';

export interface DeleteConfirmationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when deletion is confirmed */
  onConfirm: () => void;
  /** The exact name user must type to confirm (case-sensitive) */
  itemName: string;
  /** The type of item being deleted (e.g., "tenant", "user") */
  itemType: string;
  /** Whether the delete operation is in progress */
  loading?: boolean;
  /** Custom warning message (optional) */
  warningMessage?: string;
  /** Additional info to display (optional) */
  additionalInfo?: React.ReactNode;
}

/**
 * DeleteConfirmationDialog Component
 *
 * A safety dialog that requires users to type the exact name of the item
 * they want to delete before enabling the delete button. This prevents
 * accidental deletions of important resources.
 *
 * @example
 * ```tsx
 * <DeleteConfirmationDialog
 *   open={deleteDialogOpen}
 *   onClose={() => setDeleteDialogOpen(false)}
 *   onConfirm={handleDelete}
 *   itemName={tenantToDelete.name}
 *   itemType="tenant"
 *   loading={isDeleting}
 * />
 * ```
 */
export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  itemName,
  itemType,
  loading = false,
  warningMessage,
  additionalInfo,
}) => {
  const [confirmationInput, setConfirmationInput] = useState('');

  // Reset input when dialog opens/closes
  useEffect(() => {
    if (open) {
      setConfirmationInput('');
    }
  }, [open]);

  // Check if input matches the item name exactly (case-sensitive)
  const isConfirmationValid = confirmationInput === itemName;

  const handleConfirm = () => {
    if (isConfirmationValid) {
      onConfirm();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && isConfirmationValid && !loading) {
      handleConfirm();
    }
  };

  const defaultWarningMessage = `This action cannot be undone. The ${itemType} will be permanently deleted.`;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid="delete-confirmation-dialog"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningAmberIcon color="error" />
          <Typography variant="h6" component="span">
            Delete {itemType}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          {warningMessage || defaultWarningMessage}
        </Alert>

        <Typography variant="body1" gutterBottom>
          Are you sure you want to delete <strong>{itemName}</strong>?
        </Typography>

        {additionalInfo && <Box sx={{ mt: 2, mb: 2 }}>{additionalInfo}</Box>}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
          To confirm, please type the {itemType} name exactly:
        </Typography>

        <TextField
          fullWidth
          placeholder={itemName}
          value={confirmationInput}
          onChange={(e) => setConfirmationInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          autoFocus
          error={confirmationInput.length > 0 && !isConfirmationValid}
          helperText={
            confirmationInput.length > 0 && !isConfirmationValid ? 'Name does not match' : ' '
          }
          inputProps={{
            'data-testid': 'confirm-name-input',
            'aria-label': `Type ${itemName} to confirm deletion`,
          }}
          sx={{ mt: 1 }}
        />
      </DialogContent>

      <DialogFooter>
        <SecondaryButton onClick={onClose} disabled={loading} data-testid="cancel-delete-btn">
          Cancel
        </SecondaryButton>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={!isConfirmationValid || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          data-testid="confirm-delete-btn"
        >
          {loading ? 'Deleting...' : `Delete ${itemType}`}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default DeleteConfirmationDialog;
